import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from './Navigation';
import { api } from '../services/api';
import './Accounts.css';

interface Account {
  accountId: number;
  accountName: string;
  accountType: string;
  bankName?: string;
  currency: string;
  openingBalance: number;
  currentBalance: number;
  isDefault: boolean;
  isActive: boolean;
  userId: number;
}

const Accounts: React.FC = () => {
  const navigate = useNavigate();
  const userId = localStorage.getItem('userId');
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Form state
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingAccount, setEditingAccount] = useState<Account | null>(null);
  const [formData, setFormData] = useState({
    accountName: '',
    accountType: 'Savings',
    bankName: '',
    currency: 'KWD',
    openingBalance: '0'
  });

  const fetchAccounts = useCallback(async () => {
    if (!userId) return;
    
    try {
      const response = await api.get(`/accounts/user/${userId}`);
      if (!response.ok) throw new Error('Failed to fetch accounts');
      const data = await response.json();
      setAccounts(data);
    } catch (err) {
      console.error('Error fetching accounts:', err);
      setError('Failed to load accounts');
    } finally {
      setIsLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (!userId) {
      navigate('/');
      return;
    }
    fetchAccounts();
  }, [userId, navigate, fetchAccounts]);

  const handleAddAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId) return;

    if (!formData.accountName.trim()) {
      setError('Please enter account name');
      return;
    }

    try {
      const response = await api.post(`/accounts/user/${userId}`, {
        accountName: formData.accountName.trim(),
        accountType: formData.accountType,
        bankName: formData.bankName.trim() || null,
        currency: formData.currency,
        openingBalance: parseFloat(formData.openingBalance) || 0,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ errorMessage: 'Failed to create account' }));
        throw new Error(errorData.errorMessage || 'Failed to create account');
      }

      await fetchAccounts();
      closeModal();
      setSuccess('Account created successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      console.error('Error adding account:', err);
      setError(err.message || 'Failed to add account');
    }
  };

  const handleUpdateAccount = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!userId || !editingAccount) return;

    if (!formData.accountName.trim()) {
      setError('Please enter account name');
      return;
    }

    try {
      const response = await api.put(`/accounts/${editingAccount.accountId}`, {
        accountName: formData.accountName.trim(),
        accountType: formData.accountType,
        bankName: formData.bankName.trim() || null,
        currency: formData.currency,
        openingBalance: parseFloat(formData.openingBalance) || 0,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ errorMessage: 'Failed to update account' }));
        throw new Error(errorData.errorMessage || 'Failed to update account');
      }

      await fetchAccounts();
      closeModal();
      setSuccess('Account updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err: any) {
      console.error('Error updating account:', err);
      setError(err.message || 'Failed to update account');
    }
  };

  const handleDeactivateAccount = async (accountId: number) => {
    if (!window.confirm('Are you sure you want to deactivate this account?')) {
      return;
    }

    try {
      const response = await api.patch(`/accounts/${accountId}/deactivate`);

      if (!response.ok) throw new Error('Failed to deactivate account');

      await fetchAccounts();
      setSuccess('Account deactivated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deactivating account:', err);
      setError('Failed to deactivate account');
    }
  };

  const openEditModal = (account: Account) => {
    setEditingAccount(account);
    setFormData({
      accountName: account.accountName,
      accountType: account.accountType,
      bankName: account.bankName || '',
      currency: account.currency,
      openingBalance: account.openingBalance.toString(),
    });
    setShowAddModal(true);
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingAccount(null);
    setFormData({
      accountName: '',
      accountType: 'Savings',
      bankName: '',
      currency: 'KWD',
      openingBalance: '0'
    });
    setError('');
  };

  const formatCurrency = (amount: number, currency: string = 'KWD') => {
    return new Intl.NumberFormat('en-KW', {
      style: 'decimal',
      minimumFractionDigits: 3,
      maximumFractionDigits: 3,
    }).format(amount) + ' ' + currency;
  };

  if (isLoading) {
    return (
      <div className="accounts-container">
        <Navigation />
        <div className="loading">Loading accounts...</div>
      </div>
    );
  }

  const activeAccounts = accounts.filter(acc => acc.isActive);

  return (
    <div className="accounts-container">
      <Navigation />
      <div className="accounts-content">
        <div className="accounts-header-section">
          <h1 className="accounts-title">Accounts</h1>
          <button className="add-button" onClick={() => setShowAddModal(true)}>
            + Add Account
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}
        {success && <div className="success-message">{success}</div>}

        <div className="accounts-list">
          {activeAccounts.length === 0 ? (
            <div className="empty-state">
              <p>No accounts yet. Create your first account!</p>
            </div>
          ) : (
            activeAccounts.map((account) => (
              <div key={account.accountId} className="account-item">
                <div className="account-info">
                  <div className="account-main">
                    <h3 className="account-name">{account.accountName}</h3>
                    {account.isDefault && (
                      <span className="default-badge">Default</span>
                    )}
                  </div>
                  <div className="account-details">
                    <span className="account-type">{account.accountType}</span>
                    {account.bankName && (
                      <span className="bank-name">{account.bankName}</span>
                    )}
                    <span className="account-currency">{account.currency}</span>
                  </div>
                </div>
                <div className="account-balance-section">
                  <p className="account-balance">
                    {formatCurrency(account.currentBalance, account.currency)}
                  </p>
                  <div className="account-actions">
                    <button
                      className="edit-button"
                      onClick={() => openEditModal(account)}
                    >
                      Edit
                    </button>
                    {!account.isDefault && (
                      <button
                        className="deactivate-button"
                        onClick={() => handleDeactivateAccount(account.accountId)}
                      >
                        Deactivate
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="modal-overlay" onClick={closeModal}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2 className="modal-title">
                {editingAccount ? 'Edit Account' : 'Add New Account'}
              </h2>
              <button className="modal-close" onClick={closeModal}>×</button>
            </div>

            <form
              onSubmit={editingAccount ? handleUpdateAccount : handleAddAccount}
              className="account-form"
            >
              <div className="form-group">
                <label htmlFor="accountName" className="form-label">
                  Account Name *
                </label>
                <input
                  type="text"
                  id="accountName"
                  className="form-input"
                  placeholder="e.g., Main Savings, Checking Account"
                  value={formData.accountName}
                  onChange={(e) => {
                    setFormData({ ...formData, accountName: e.target.value });
                    setError('');
                  }}
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="accountType" className="form-label">
                  Account Type *
                </label>
                <select
                  id="accountType"
                  className="form-input"
                  value={formData.accountType}
                  onChange={(e) => {
                    setFormData({ ...formData, accountType: e.target.value });
                    setError('');
                  }}
                  required
                >
                  <option value="Savings">Savings</option>
                  <option value="Salary">Salary</option>
                  <option value="Credit Card">Credit Card</option>
                  <option value="Investment">Investment</option>
                  <option value="Cash">Cash</option>
                  <option value="Other">Other</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="bankName" className="form-label">
                  Bank Name (Optional)
                </label>
                <input
                  type="text"
                  id="bankName"
                  className="form-input"
                  placeholder="e.g., National Bank of Kuwait"
                  value={formData.bankName}
                  onChange={(e) => {
                    setFormData({ ...formData, bankName: e.target.value });
                    setError('');
                  }}
                />
              </div>

              <div className="form-group">
                <label htmlFor="currency" className="form-label">
                  Currency *
                </label>
                <select
                  id="currency"
                  className="form-input"
                  value={formData.currency}
                  onChange={(e) => {
                    setFormData({ ...formData, currency: e.target.value });
                    setError('');
                  }}
                  required
                >
                  <option value="KWD">KWD</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                  <option value="GBP">GBP</option>
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="openingBalance" className="form-label">
                  Opening Balance *
                </label>
                <input
                  type="number"
                  id="openingBalance"
                  className="form-input"
                  placeholder="0.000"
                  step="0.001"
                  value={formData.openingBalance}
                  onChange={(e) => {
                    setFormData({ ...formData, openingBalance: e.target.value });
                    setError('');
                  }}
                  required
                />
              </div>

              <button type="submit" className="submit-button">
                {editingAccount ? 'Update Account' : 'Create Account'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Accounts;

